import { EditableField } from '../../../../../components/field/field';
import { Typography } from '../../../../../components/typography/typography';
import classNames from 'classnames';
import styles from './product-item.module.css';
import { memo } from 'react';
import { ProductItemState } from '../../hooks/use-product-item-data';
import deepEqual from 'deep-equal';
import { ProductItemStateFee } from '../../hooks/use-product-item-data';
import { ProductItemFeesReturn } from '../../hooks/use-product-item-fees';

interface ProductItemFeesProps extends Pick<ProductItemState, 'fees' | 'productId' | 'isEditing'> {
  updateFee: ProductItemFeesReturn['updateFee'];
  resetFee: ProductItemFeesReturn['resetFee'];
}

export const ProductItemFees = memo<ProductItemFeesProps>(
  ({ fees, productId, isEditing, updateFee, resetFee }) => {
    if (fees.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-1">
        {fees.map((fee) => (
          <ProductItemFee
            key={`product-${productId}-fee-${fee.productLineItemId}-${fee.productFeeId}`}
            fee={fee}
            isEditing={isEditing}
            updateFee={updateFee}
            resetFee={resetFee}
          />
        ))}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      deepEqual(prevProps.fees, nextProps.fees) &&
      prevProps.productId === nextProps.productId &&
      prevProps.isEditing === nextProps.isEditing
    );
  },
);

const ProductItemFee = memo<{
  fee: ProductItemStateFee;
  isEditing: boolean;
  updateFee: (fee: ProductItemStateFee, amount: number) => void;
  resetFee: (fee: ProductItemStateFee) => void;
}>(
  ({ fee, isEditing, updateFee, resetFee }) => {
    const key = `product-item-fee-${fee.productLineItemId}-${fee.productFeeId}`;
    if (isEditing && fee.canEdit) {
      return (
        <div key={key} className={classNames('flex flex-wrap gap-1')} data-cy={`${key}-field`}>
          <Typography
            type="text-xs"
            weight="medium"
            className={classNames(styles.fieldContentText)}
            data-cy={`${key}-field-label`}
          >
            {fee.title}
          </Typography>
          <EditableField
            type="number"
            label={fee.title}
            value={fee.amount.toUnit()}
            formattedValue={fee.amount.toFormat()}
            canReset={fee.hasCustomAmount}
            resetPosition="right"
            onReset={() => resetFee(fee)}
            onChange={(value: number) => updateFee(fee, value)}
          />
        </div>
      );
    }

    return (
      <Typography
        key={key}
        dataCy={`${key}-info`}
        type="text-xs"
        weight="medium"
        className={classNames(styles.fieldContentText)}
      >
        {`${fee.title} ${fee.amount.toFormat()}`}
      </Typography>
    );
  },
  (prevProps, nextProps) => {
    return deepEqual(prevProps.fee, nextProps.fee) && prevProps.isEditing === nextProps.isEditing;
  },
);
